import DOMPurify from "dompurify";
import { findByTitle } from "@/lib/api";

export const PALETTE = [
  ["Amber", "#f59e0b"], ["Gold", "#fde68a"], ["Ember", "#fb923c"], ["Crimson", "#ef4444"], ["Rose", "#f472b6"], ["Violet", "#a78bfa"],
  ["Indigo", "#818cf8"], ["Sky", "#38bdf8"], ["Teal", "#2dd4bf"], ["Moss", "#86efac"], ["Bone", "#e7e5e4"], ["Ash", "#94a3b8"],
];
export const FONTS = [
  ["Default", ""], ["Cormorant Garamond", "Cormorant Garamond"], ["EB Garamond", "EB Garamond"], ["Cinzel", "Cinzel"],
  ["IM Fell English", "IM Fell English"], ["Caveat", "Caveat"], ["JetBrains Mono", "JetBrains Mono"],
];
export const SIZES = [["Small", "0.875em"], ["Normal", ""], ["Large", "1.3em"]];

const parse = (html) => new DOMParser().parseFromString(html || "", "text/html");
const unwrap = (el) => el.replaceWith(...el.childNodes);

// Keep structure and bold/italic/underline/strike from Docs or Word, drop their fonts, colours and classes
export const cleanPaste = (html) => {
  const doc = parse(html);
  doc.querySelectorAll('b[id^="docs-internal-guid"]').forEach(unwrap);
  doc.querySelectorAll("b, strong").forEach((el) => { if (/normal|400/.test(el.style.fontWeight)) unwrap(el); });
  doc.querySelectorAll("[style]").forEach((el) => {
    const st = el.style, deco = `${st.textDecoration} ${st.textDecorationLine}`;
    const wraps = [];
    if (/bold|[6-9]00/.test(st.fontWeight) && !/^(H\d|B|STRONG)$/.test(el.tagName)) wraps.push("strong");
    if (st.fontStyle === "italic" && !/^(I|EM)$/.test(el.tagName)) wraps.push("em");
    if (deco.includes("underline") && el.tagName !== "A") wraps.push("u");
    if (deco.includes("line-through")) wraps.push("s");
    wraps.forEach((tag) => { const w = doc.createElement(tag); w.append(...el.childNodes); el.append(w); });
    el.removeAttribute("style");
  });
  doc.querySelectorAll("font").forEach(unwrap);
  doc.querySelectorAll("[class], [color], [face], [bgcolor]").forEach((el) => ["class", "color", "face", "bgcolor"].forEach((a) => el.removeAttribute(a)));
  return doc.body.innerHTML;
};

export const htmlToText = (html) => {
  const doc = parse(html);
  doc.querySelectorAll("br").forEach((b) => b.replaceWith("\n"));
  doc.querySelectorAll("li").forEach((li) => li.prepend("• "));
  doc.querySelectorAll("p, h1, h2, h3, h4, li, blockquote, pre, tr").forEach((el) => el.append("\n"));
  return doc.body.textContent.replace(/\n{3,}/g, "\n\n").trim();
};

const WIKI_RE = /\[\[([^[\]|]+)(?:\|([^[\]]+))?\]\]/g;
const ON = "text-amber-400 bg-amber-950/40 border-amber-800/50 hover:bg-amber-900/60";
const OFF = "text-rose-300 bg-rose-950/30 border-dashed border-rose-800/60 hover:bg-rose-900/40";

// Sanitised HTML with [[Page]] text turned into clickable wiki links
export const renderRich = (html, pages, readOnly) => {
  const doc = parse(DOMPurify.sanitize(html || ""));
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  const hits = [];
  while (walker.nextNode()) if (walker.currentNode.nodeValue.includes("[[")) hits.push(walker.currentNode);
  hits.forEach((node) => {
    const text = node.nodeValue, frag = doc.createDocumentFragment();
    let last = 0;
    for (const m of text.matchAll(WIKI_RE)) {
      frag.append(text.slice(last, m.index));
      const title = m[1].trim(), target = findByTitle(pages, title);
      const el = doc.createElement(target || !readOnly ? "button" : "span");
      el.textContent = (m[2] || m[1]).trim();
      el.dataset.testid = "wiki-link";
      el.dataset.title = title;
      if (target || !readOnly) {
        el.dataset.wiki = title;
        el.type = "button";
        el.title = target ? target.summary || target.title : "Page not written yet. Click to create";
        el.className = `font-code text-[0.85em] rounded px-1.5 py-0.5 border transition-colors ${target ? ON : OFF}`;
      } else el.className = "text-slate-500 border-b border-dashed border-slate-600";
      frag.append(el);
      last = m.index + m[0].length;
    }
    frag.append(text.slice(last));
    node.replaceWith(frag);
  });
  doc.querySelectorAll("a[href]").forEach((a) => { a.target = "_blank"; a.rel = "noreferrer"; a.className = "text-amber-400 underline underline-offset-4"; });
  return doc.body.innerHTML;
};
